import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Search, Clock, Heart, Menu, X, ShoppingBag, HelpCircle, FileText, Shield, Book } from 'lucide-react';
import { useStore } from '@/store';

export function Sidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const cart = useStore((state) => state.cart);

  const mainMenuItems = [
    { name: 'Home', icon: Home, href: '/home' },
    { name: 'Search', icon: Search, href: '/products' },
    { name: 'Orders', icon: Clock, href: '/track' },
    { name: 'Wishlist', icon: Heart, href: '/wishlist' },
    { name: 'Cart', icon: ShoppingBag, href: '/cart', badge: cart.length }
  ];

  const helpMenuItems = [
    { name: 'FAQ', icon: HelpCircle, href: '/faq' },
    { name: 'Return Policy', icon: FileText, href: '/return-policy' },
    { name: 'Privacy Policy', icon: Shield, href: '/privacy-policy' },
    { name: 'Terms of Service', icon: Book, href: '/terms-of-service' }
  ];

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="lg:hidden fixed left-4 top-4 z-50 p-2 rounded-full bg-white dark:bg-gray-800 shadow-lg"
      >
        <Menu className="h-6 w-6 text-gray-900 dark:text-white" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setIsOpen(false)}
            />

            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              className="fixed left-0 top-0 bottom-0 w-64 bg-white dark:bg-gray-800 shadow-xl z-50"
            >
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Menu</h2>
                  <button onClick={() => setIsOpen(false)}>
                    <X className="h-6 w-6 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <nav className="flex-1 overflow-y-auto p-4">
                  <div className="mb-8">
                    {mainMenuItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl mb-2 transition-colors ${
                          location.pathname === item.href
                            ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-primary dark:text-brand-light'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <item.icon className="h-5 w-5" />
                        <span>{item.name}</span>
                        {item.badge && item.badge > 0 && (
                          <span className="ml-auto bg-brand-primary text-white text-xs font-bold px-2 py-1 rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>

                  <div className="border-t dark:border-gray-700 pt-4">
                    <h3 className="px-4 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      Help & Support
                    </h3>
                    {helpMenuItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl mb-2 transition-colors ${
                          location.pathname === item.href
                            ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-primary dark:text-brand-light'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <item.icon className="h-5 w-5" />
                        <span>{item.name}</span>
                      </Link>
                    ))}
                  </div>
                </nav>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}